import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Search, Image, X, Filter, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface GalleryPhoto {
  id: string;
  title: string | null;
  description: string | null;
  photo_url: string;
  member_id: string | null;
  category: string | null;
  created_at: string;
  member_name?: string;
}

interface FamilyMember {
  id: string;
  name: string;
}

const Gallery = () => {
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [memberFilter, setMemberFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedPhoto, setSelectedPhoto] = useState<GalleryPhoto | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const [photosRes, membersRes] = await Promise.all([
        supabase.from("gallery_photos").select("*").order("created_at", { ascending: false }),
        supabase.from("family_members").select("id, name").order("name"),
      ]);

      const membersData = (membersRes.data || []) as FamilyMember[];
      setMembers(membersData);

      const photosData = (photosRes.data || []).map((p: any) => ({
        ...p,
        member_name: membersData.find(m => m.id === p.member_id)?.name || null,
      }));
      setPhotos(photosData);
      setLoading(false);
    };
    fetchData();
  }, []);

  const categories = [...new Set(photos.map(p => p.category).filter(Boolean))] as string[];

  const filtered = photos.filter(p => {
    if (search && !(p.title?.includes(search) || p.description?.includes(search) || p.member_name?.includes(search))) return false;
    if (memberFilter !== "all" && p.member_id !== memberFilter) return false;
    if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
    return true;
  });

  const categoryLabels: Record<string, string> = {
    general: "عامة",
    event: "مناسبات",
    old: "قديمة",
    memory: "ذكريات",
  };

  return (
    <div className="min-h-screen pt-28 pb-12 px-4">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-primary/5 blur-[150px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] rounded-full bg-accent/3 blur-[120px]" />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/20">
              <Image className="h-7 w-7 text-primary" />
            </div>
          </div>
          <h1 className="font-amiri text-5xl md:text-6xl font-bold gold-text mb-3">
            معرض الصور
          </h1>
          <p className="text-muted-foreground text-lg font-tajawal">صور وذكريات عائلة أبوليلة</p>
          <div className="flex items-center justify-center gap-4 mt-6">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-primary/40" />
            <Sparkles className="w-5 h-5 text-primary/50" />
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-primary/40" />
          </div>
        </motion.div>

        {/* Search & Filters */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="space-y-4 mb-8 max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="ابحث بالعنوان أو الوصف أو اسم الفرد..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pr-10 bg-secondary/50 border-primary/20 rounded-xl"
              dir="rtl"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Member filter */}
          {members.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="h-3.5 w-3.5" />
                <span>حسب الفرد:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={memberFilter === "all" ? "default" : "outline"}
                  className={`cursor-pointer rounded-lg transition-all ${memberFilter === "all" ? "gold-gradient text-primary-foreground" : "border-primary/20 hover:bg-primary/10"}`}
                  onClick={() => setMemberFilter("all")}
                >
                  الكل
                </Badge>
                {members.filter(m => photos.some(p => p.member_id === m.id)).map(m => (
                  <Badge
                    key={m.id}
                    variant={memberFilter === m.id ? "default" : "outline"}
                    className={`cursor-pointer rounded-lg transition-all ${memberFilter === m.id ? "gold-gradient text-primary-foreground" : "border-primary/20 hover:bg-primary/10"}`}
                    onClick={() => setMemberFilter(m.id)}
                  >
                    {m.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Category filter */}
          {categories.length > 1 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="h-3.5 w-3.5" />
                <span>حسب التصنيف:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={categoryFilter === "all" ? "default" : "outline"}
                  className={`cursor-pointer rounded-lg transition-all ${categoryFilter === "all" ? "gold-gradient text-primary-foreground" : "border-primary/20 hover:bg-primary/10"}`}
                  onClick={() => setCategoryFilter("all")}
                >
                  الكل
                </Badge>
                {categories.map(c => (
                  <Badge
                    key={c}
                    variant={categoryFilter === c ? "default" : "outline"}
                    className={`cursor-pointer rounded-lg transition-all ${categoryFilter === c ? "gold-gradient text-primary-foreground" : "border-primary/20 hover:bg-primary/10"}`}
                    onClick={() => setCategoryFilter(c)}
                  >
                    {categoryLabels[c] || c}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <p className="text-sm text-muted-foreground text-center">
            {filtered.length} صورة
          </p>
        </motion.div>

        {/* Photo Grid */}
        {loading ? (
          <div className="text-center text-muted-foreground py-20">جاري التحميل...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted-foreground py-20">
            <Image className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">لا توجد صور حالياً</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-4"
          >
            {filtered.map((photo, i) => (
              <motion.div
                key={photo.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="break-inside-avoid cursor-pointer group"
                onClick={() => setSelectedPhoto(photo)}
              >
                <div className="glass-card rounded-xl overflow-hidden border-primary/10 hover:border-primary/30 transition-all">
                  <div className="relative overflow-hidden">
                    <img
                      src={photo.photo_url}
                      alt={photo.title || "صورة"}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {(photo.title || photo.member_name) && (
                    <div className="p-3">
                      {photo.title && <p className="text-sm font-medium text-foreground truncate">{photo.title}</p>}
                      {photo.member_name && <p className="text-xs text-muted-foreground mt-0.5">{photo.member_name}</p>}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Lightbox */}
        <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
          <DialogContent className="max-w-4xl glass-card border-primary/20 rounded-2xl p-2 sm:p-4" dir="rtl">
            {selectedPhoto && (
              <div className="space-y-3">
                <img
                  src={selectedPhoto.photo_url}
                  alt={selectedPhoto.title || "صورة"}
                  className="w-full max-h-[75vh] object-contain rounded-xl"
                />
                <div className="px-2 pb-2">
                  {selectedPhoto.title && <h3 className="font-amiri text-xl gold-text">{selectedPhoto.title}</h3>}
                  {selectedPhoto.description && <p className="text-muted-foreground text-sm mt-1">{selectedPhoto.description}</p>}
                  {selectedPhoto.member_name && (
                    <Badge className="mt-2 border-primary/20" variant="outline">{selectedPhoto.member_name}</Badge>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Gallery;
